// =============================================================================
// Origin UI — src/pages/leave/LeaveReportsPage.tsx
// HR Admin leave reports: Balance Report + Usage Report.
// Phase 6 — Leave Management module.
// =============================================================================

import { useState, useEffect } from 'react';
import { leaveApi, type LeaveBalanceReportItem, type LeaveUsageReportItem } from '@/services/leaveApi';
import { api } from '@/services/apiService';
import { PageHeader, Card, ErrorBanner, LoadingSpinner } from '@/components/common';
import styles from './leave.module.css';

type ReportTab = 'balance' | 'usage';

interface RefItem { name: string; id: number; }

export function LeaveReportsPage() {
  const [activeTab, setActiveTab] = useState<ReportTab>('balance');
  const [departments, setDepartments] = useState<RefItem[]>([]);
  const [offices, setOffices] = useState<RefItem[]>([]);
  const [employeeList, setEmployeeList] = useState<{ employeeId: string; name: string; number: string }[]>([]);

  useEffect(() => {
    Promise.all([
      api.get<any[]>('/api/admin/departments'),
      api.get<any[]>('/api/admin/office-locations'),
      api.get<any[]>('/api/employees'),
    ]).then(([d, o, emps]) => {
      setDepartments(d.filter((x: any) => x.isActive).map((x: any) => ({ name: x.name, id: x.departmentId })));
      setOffices(o.filter((x: any) => x.isActive).map((x: any) => ({ name: x.name, id: x.officeLocationId })));
      setEmployeeList(
        emps.map((e: any) => ({
          employeeId: e.employeeId,
          name: e.displayName || ((e.preferredFirstName || e.firstName) + ' ' + (e.preferredLastName || e.lastName)),
          number: e.employeeNumber,
        })).sort((a: any, b: any) => a.name.localeCompare(b.name))
      );
    }).catch(() => {});
  }, []);

  return (
    <div className={styles.page}>
      <PageHeader title="Leave Reports" subtitle="Company-wide leave balances and usage analysis" />

      <div className={styles.tabs}>
        <button className={`${styles.tab} ${activeTab === 'balance' ? styles.tabActive : ''}`} onClick={() => setActiveTab('balance')}>
          Balance Report
        </button>
        <button className={`${styles.tab} ${activeTab === 'usage' ? styles.tabActive : ''}`} onClick={() => setActiveTab('usage')}>
          Usage Report
        </button>
      </div>

      {activeTab === 'balance' && <BalanceReportTab departments={departments} offices={offices} employeeList={employeeList} />}
      {activeTab === 'usage' && <UsageReportTab departments={departments} offices={offices} employeeList={employeeList} />}
    </div>
  );
}

// =============================================================================
// Balance Report
// =============================================================================

type SortKey = 'name' | 'department' | 'office' | string; // string for leave type codes

interface TabProps {
  departments: RefItem[];
  offices: RefItem[];
  employeeList: { employeeId: string; name: string; number: string }[];
}

function BalanceReportTab({ departments, offices, employeeList }: TabProps) {
  const [data, setData] = useState<LeaveBalanceReportItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filterDept, setFilterDept] = useState<number | ''>('');
  const [filterOffice, setFilterOffice] = useState<number | ''>('');
  const [filterEmployee, setFilterEmployee] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  async function loadReport() {
    try {
      setLoading(true); setError('');
      const result = await leaveApi.getBalanceReport(filterDept || undefined, filterOffice || undefined);
      setData(result);
      setLoaded(true);
    } catch (err: any) { setError(err.message || 'Failed to load report'); }
    finally { setLoading(false); }
  }

  // Helper: get balance for an employee by leave type code
  function getMergedBalance(emp: LeaveBalanceReportItem, code: string) {
    const b = emp.balances.find(bal => bal.leaveTypeCode === code);
    if (!b) return null;
    return { ...b, totalDue: b.entitlement + b.carryForward };
  }

  // Compute dynamic leave type columns: union of all types where at least one employee has entitlement > 0
  const leaveTypeCodes: { code: string; name: string }[] = [];
  if (data.length > 0) {
    const seen = new Set<string>();
    for (const emp of data) {
      for (const b of emp.balances) {
        if (!seen.has(b.leaveTypeCode) && b.entitlement > 0) {
          seen.add(b.leaveTypeCode);
          leaveTypeCodes.push({ code: b.leaveTypeCode, name: b.leaveTypeName });
        }
      }
    }
    // Sort: ANN first, then alphabetical
    leaveTypeCodes.sort((a, b) => {
      if (a.code === 'ANN') return -1;
      if (b.code === 'ANN') return 1;
      return a.code.localeCompare(b.code);
    });
  }

  // Filter by employee
  const filtered = filterEmployee
    ? data.filter(e => e.employeeId === filterEmployee)
    : data;

  // Sort
  function toggleSort(key: SortKey) {
    if (sortKey === key) { setSortDir(d => d === 'asc' ? 'desc' : 'asc'); }
    else { setSortKey(key); setSortDir('asc'); }
  }

  const sorted = [...filtered].sort((a, b) => {
    let av: string | number;
    let bv: string | number;
    if (sortKey === 'name') {
      av = a.employeeName.toLowerCase(); bv = b.employeeName.toLowerCase();
    } else if (sortKey === 'department') {
      av = a.departmentName.toLowerCase(); bv = b.departmentName.toLowerCase();
    } else if (sortKey === 'office') {
      av = a.officeName.toLowerCase(); bv = b.officeName.toLowerCase();
    } else {
      // Leave type code — sort by available
      const ba = getMergedBalance(a, sortKey);
      const bb = getMergedBalance(b, sortKey);
      av = ba ? ba.available : -1;
      bv = bb ? bb.available : -1;
    }
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  function sortIndicator(key: SortKey) {
    if (sortKey !== key) return null;
    return <span style={{ marginLeft: 4 }}>{sortDir === 'asc' ? '▲' : '▼'}</span>;
  }

  function exportCSV() {
    if (sorted.length === 0) return;
    const headers = ['OQT ID', 'Employee #', 'Name', 'Department', 'Office',
      ...leaveTypeCodes.flatMap(lt => [`${lt.code} Entitlement`, `${lt.code} CarryFwd`, `${lt.code} TotalDue`, `${lt.code} Taken`, `${lt.code} Committed`, `${lt.code} Pending`, `${lt.code} Available`])
    ];
    const rows = sorted.map(emp => [
      (emp as any).oqtId || '', emp.employeeNumber, emp.employeeName, emp.departmentName, emp.officeName,
      ...leaveTypeCodes.flatMap(lt => {
        const b = getMergedBalance(emp, lt.code);
        return b ? [String(b.entitlement), String(b.carryForward), String(b.totalDue), String(b.taken), String(b.committed), String(b.pending), String(b.available)] : ['', '', '', '', '', '', ''];
      })
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.map(c => `"${c}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `leave_balance_report_${new Date().toISOString().split('T')[0]}.csv`;
    a.click(); URL.revokeObjectURL(url);
  }

  return (
    <div>
      {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <select className={styles.filterSelect} value={filterDept} onChange={e => setFilterDept(e.target.value ? Number(e.target.value) : '')}>
          <option value="">All departments</option>
          {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        <select className={styles.filterSelect} value={filterOffice} onChange={e => setFilterOffice(e.target.value ? Number(e.target.value) : '')}>
          <option value="">All offices</option>
          {offices.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
        <select className={styles.filterSelect} value={filterEmployee} onChange={e => setFilterEmployee(e.target.value)} style={{ minWidth: 220 }}>
          <option value="">All employees</option>
          {employeeList.map(emp => (
            <option key={emp.employeeId} value={emp.employeeId}>{emp.name} ({emp.number})</option>
          ))}
        </select>
        <button className={styles.createBtn} onClick={loadReport} disabled={loading}>
          {loading ? 'Loading...' : 'Generate Report'}
        </button>
        {data.length > 0 && (
          <button className={styles.btnSecondary} onClick={exportCSV}>Export CSV</button>
        )}
      </div>

      {loading && <LoadingSpinner message="Generating balance report..." />}

      {loaded && !loading && (
        <Card padding={false}>
          {sorted.length === 0 ? (
            <div className={styles.emptyTab}>No employees found for the selected filters.</div>
          ) : (
            <div className={styles.gridContainer}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th onClick={() => toggleSort('name')} style={{ cursor: 'pointer', userSelect: 'none' }}>Employee{sortIndicator('name')}</th>
                    <th onClick={() => toggleSort('department')} style={{ cursor: 'pointer', userSelect: 'none' }}>Department{sortIndicator('department')}</th>
                    <th onClick={() => toggleSort('office')} style={{ cursor: 'pointer', userSelect: 'none' }}>Office{sortIndicator('office')}</th>
                    {leaveTypeCodes.map(lt => (
                      <th key={lt.code} onClick={() => toggleSort(lt.code)} style={{ textAlign: 'right', cursor: 'pointer', userSelect: 'none' }}>
                        {lt.code === 'ANN' ? 'ANN' : lt.code}{sortIndicator(lt.code)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sorted.map(emp => (
                    <tr key={emp.employeeId}>
                      <td><strong>{emp.employeeName}</strong> <span className={styles.muted}>{(emp as any).oqtId || emp.employeeNumber}</span></td>
                      <td>{emp.departmentName}</td>
                      <td>{emp.officeName}</td>
                      {leaveTypeCodes.map(lt => {
                        const b = getMergedBalance(emp, lt.code);
                        if (!b || b.totalDue === 0) {
                          return <td key={lt.code} style={{ textAlign: 'right' }}><span className={styles.muted}>—</span></td>;
                        }
                        const parts = [`Entitlement: ${b.entitlement}`];
                        if (b.carryForward > 0) parts.push(`Carry Fwd: ${b.carryForward}`);
                        parts.push(`Taken: ${b.taken}`, `Committed: ${b.committed}`, `Pending: ${b.pending}`);
                        return (
                          <td key={lt.code} style={{ textAlign: 'right' }} title={parts.join(', ')}>
                            <span style={{ fontWeight: 700, color: b.available < 0 ? '#E53E3E' : 'var(--turquoise-dark)' }}>{b.available}</span>
                            <span className={styles.muted}> / {b.totalDue}</span>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {sorted.length > 0 && (
            <div style={{ padding: '12px 20px', borderTop: '1px solid var(--gray-200)', fontSize: 13, color: 'var(--gray-500)' }}>
              {sorted.length} employee{sorted.length !== 1 ? 's' : ''}. Showing Available / Total Due (Entitlement + Carry Forward). Hover cells for breakdown.
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

// =============================================================================
// Usage Report
// =============================================================================

type UsageSortKey = 'name' | 'department' | 'type' | 'start' | 'end' | 'days';

function UsageReportTab({ departments, offices, employeeList }: TabProps) {
  const [data, setData] = useState<LeaveUsageReportItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filterDept, setFilterDept] = useState<number | ''>('');
  const [filterOffice, setFilterOffice] = useState<number | ''>('');
  const [filterEmployee, setFilterEmployee] = useState('');
  const [startDate, setStartDate] = useState(new Date().getFullYear() + '-01-01');
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [loaded, setLoaded] = useState(false);
  const [sortKey, setSortKey] = useState<UsageSortKey>('start');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  async function loadReport() {
    try {
      setLoading(true); setError('');
      const result = await leaveApi.getUsageReport(startDate, endDate, filterDept || undefined, filterOffice || undefined);
      setData(result);
      setLoaded(true);
    } catch (err: any) { setError(err.message || 'Failed to load report'); }
    finally { setLoading(false); }
  }

  // Filter by employee (using employeeNumber since LeaveUsageReportItem has no employeeId)
  const selectedNumber = filterEmployee
    ? employeeList.find(e => e.employeeId === filterEmployee)?.number || ''
    : '';
  const filtered = selectedNumber
    ? data.filter(e => e.employeeNumber === selectedNumber)
    : data;

  const totalDays = filtered.reduce((sum, r) => sum + r.totalWorkingDays, 0);

  // Sort
  function toggleSort(key: UsageSortKey) {
    if (sortKey === key) { setSortDir(d => d === 'asc' ? 'desc' : 'asc'); }
    else { setSortKey(key); setSortDir('asc'); }
  }

  const sorted = [...filtered].sort((a, b) => {
    let av: string | number;
    let bv: string | number;
    if (sortKey === 'name') {
      av = a.employeeName.toLowerCase(); bv = b.employeeName.toLowerCase();
    } else if (sortKey === 'department') {
      av = a.departmentName.toLowerCase(); bv = b.departmentName.toLowerCase();
    } else if (sortKey === 'type') {
      av = a.leaveTypeCode.toLowerCase(); bv = b.leaveTypeCode.toLowerCase();
    } else if (sortKey === 'start') {
      av = a.startDate; bv = b.startDate;
    } else if (sortKey === 'end') {
      av = a.endDate; bv = b.endDate;
    } else {
      av = a.totalWorkingDays; bv = b.totalWorkingDays;
    }
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  function sortIndicator(key: UsageSortKey) {
    if (sortKey !== key) return null;
    return <span style={{ marginLeft: 4 }}>{sortDir === 'asc' ? '▲' : '▼'}</span>;
  }

  function exportCSV() {
    if (sorted.length === 0) return;
    const headers = ['OQT ID', 'Employee #', 'Name', 'Department', 'Office', 'Leave Type', 'Start', 'End', 'Days', 'Status'];
    const rows = sorted.map(r => [
      (r as any).oqtId || '', r.employeeNumber, r.employeeName, r.departmentName, r.officeName,
      r.leaveTypeName, r.startDate.split('T')[0], r.endDate.split('T')[0], r.totalWorkingDays, r.status
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.map(c => `"${c}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `leave_usage_report_${startDate}_${endDate}.csv`;
    a.click(); URL.revokeObjectURL(url);
  }

  return (
    <div>
      {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <input type="date" className={styles.formInput} value={startDate} onChange={e => setStartDate(e.target.value)} style={{ width: 160 }} />
        <span className={styles.muted}>to</span>
        <input type="date" className={styles.formInput} value={endDate} onChange={e => setEndDate(e.target.value)} style={{ width: 160 }} />
        <select className={styles.filterSelect} value={filterDept} onChange={e => setFilterDept(e.target.value ? Number(e.target.value) : '')}>
          <option value="">All departments</option>
          {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        <select className={styles.filterSelect} value={filterOffice} onChange={e => setFilterOffice(e.target.value ? Number(e.target.value) : '')}>
          <option value="">All offices</option>
          {offices.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
        <select className={styles.filterSelect} value={filterEmployee} onChange={e => setFilterEmployee(e.target.value)} style={{ minWidth: 220 }}>
          <option value="">All employees</option>
          {employeeList.map(emp => (
            <option key={emp.employeeId} value={emp.employeeId}>{emp.name} ({emp.number})</option>
          ))}
        </select>
        <button className={styles.createBtn} onClick={loadReport} disabled={loading}>
          {loading ? 'Loading...' : 'Generate Report'}
        </button>
        {data.length > 0 && (
          <button className={styles.btnSecondary} onClick={exportCSV}>Export CSV</button>
        )}
      </div>

      {loading && <LoadingSpinner message="Generating usage report..." />}

      {loaded && !loading && (
        <Card padding={false}>
          {sorted.length === 0 ? (
            <div className={styles.emptyTab}>No approved leave found for the selected period and filters.</div>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th onClick={() => toggleSort('name')} style={{ cursor: 'pointer', userSelect: 'none' }}>Employee{sortIndicator('name')}</th>
                  <th onClick={() => toggleSort('department')} style={{ cursor: 'pointer', userSelect: 'none' }}>Department{sortIndicator('department')}</th>
                  <th onClick={() => toggleSort('type')} style={{ cursor: 'pointer', userSelect: 'none' }}>Type{sortIndicator('type')}</th>
                  <th onClick={() => toggleSort('start')} style={{ cursor: 'pointer', userSelect: 'none' }}>Start{sortIndicator('start')}</th>
                  <th onClick={() => toggleSort('end')} style={{ cursor: 'pointer', userSelect: 'none' }}>End{sortIndicator('end')}</th>
                  <th onClick={() => toggleSort('days')} style={{ textAlign: 'right', cursor: 'pointer', userSelect: 'none' }}>Days{sortIndicator('days')}</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map(r => (
                  <tr key={r.leaveRequestId}>
                    <td><strong>{r.employeeName}</strong> <span className={styles.muted}>{(r as any).oqtId || r.employeeNumber}</span></td>
                    <td>{r.departmentName}</td>
                    <td><span className={styles.leaveTypeTag}>{r.leaveTypeCode}</span> {r.leaveTypeName}</td>
                    <td>{new Date(r.startDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                    <td>{new Date(r.endDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>{r.totalWorkingDays}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {sorted.length > 0 && (
            <div style={{ padding: '12px 20px', borderTop: '1px solid var(--gray-200)', fontSize: 13, color: 'var(--gray-500)' }}>
              {sorted.length} leave records. Total: {totalDays} working days taken.
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
